import React from 'react'
import { Button, Space, Tag, Tooltip } from 'antd'
import { UserAddOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import type { Employee, InviteHandler, Office, Organization } from './types'

export function inviteStatusLabel(employee: Employee): React.ReactNode {
  if (employee.invite_status === 'pending') {
    const until = employee.invite_expires_at
      ? dayjs(employee.invite_expires_at).format('DD.MM HH:mm')
      : null
    return <Tag color="gold">Ожидает{until ? ` · ${until}` : ''}</Tag>
  }
  if (employee.invite_status === 'expired') {
    return <Tag color="orange">Ссылка истекла</Tag>
  }
  if (!employee.is_active) {
    return <Tag>Неактивен</Tag>
  }
  return <Tag color="success">Активен</Tag>
}

/** Только «особые» статусы — без дубля «Активен/Неактивен», если состояние уже видно иначе. */
export function inviteExceptionLabel(employee: Employee): React.ReactNode {
  if (employee.invite_status === 'pending') {
    const until = employee.invite_expires_at
      ? dayjs(employee.invite_expires_at).format('DD.MM HH:mm')
      : null
    return <Tag color="gold">Ожидает{until ? ` · ${until}` : ''}</Tag>
  }
  if (employee.invite_status === 'expired') {
    return <Tag color="orange">Ссылка истекла</Tag>
  }
  return null
}

export function employeeInitials(employee: Employee): string {
  const source = String(employee.name || employee.login || '?').trim()
  const parts = source.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) {
    return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase()
  }
  return source.slice(0, 2).toUpperCase()
}

export function sortActiveFirst<T extends { is_active: boolean }>(items: T[]): T[] {
  return [...items].sort((a, b) => Number(b.is_active) - Number(a.is_active))
}

export const inactiveOpacityStyle: React.CSSProperties = { opacity: 0.45 }

export function InviteButton({
  org,
  office,
  onInvite,
}: {
  org: Organization
  office: Office
  onInvite: InviteHandler
}) {
  return (
    <Tooltip title="Пригласить сотрудника">
      <Button
        type="primary"
        size="small"
        icon={<UserAddOutlined />}
        style={{ background: '#52c41a', borderColor: '#52c41a' }}
        onClick={e => {
          e.stopPropagation()
          onInvite(org, office)
        }}
      />
    </Tooltip>
  )
}

export function StatusDot({ active, title }: { active: boolean; title?: string }) {
  return (
    <Tooltip title={title || (active ? 'Активен' : 'Неактивен')}>
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: active ? '#52c41a' : '#bfbfbf',
          display: 'inline-block',
          flexShrink: 0,
        }}
      />
    </Tooltip>
  )
}

export function flattenOrganizations(structure: {
  brands: { organizations: Organization[] }[]
  organizations_without_brand: Organization[]
}): Organization[] {
  const fromBrands = structure.brands.flatMap(b => b.organizations)
  return sortActiveFirst([...fromBrands, ...structure.organizations_without_brand])
}

export function EmployeeRoles({ roles }: { roles: Employee['roles'] }) {
  if (!roles.length) {
    return <span style={{ color: '#8c8c8c', fontSize: 12 }}>Роли не назначены</span>
  }
  return (
    <Space size={[4, 4]} wrap>
      {roles.map(r => (
        <Tag key={r.id} style={{ margin: 0 }}>
          {r.name}
        </Tag>
      ))}
    </Space>
  )
}
